import { Component, ElementRef, AfterViewInit, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgTemplateOutlet, DatePipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule, Table, TableFilterEvent, ColumnFilter } from 'primeng/table';
import { FilterMetadata, FilterMatchMode, FilterService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect'
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressBarModule } from 'primeng/progressbar'
import { SliderModule } from 'primeng/slider';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { ActionButtonEvent, TableColumnModel } from './tableModel';

@Component({
  selector: 'app-table-template',
  templateUrl: './table-template.component.html',
  styleUrls: ['./table-template.component.scss'],
  imports: [NgTemplateOutlet, DatePipe, DecimalPipe, CurrencyPipe, TableModule, ButtonModule,
    SelectModule, MultiSelectModule, ToastModule, FormsModule, DatePickerModule, ProgressBarModule, 
    SliderModule, InputTextModule, InputNumberModule],
  providers: [MessageService, FilterService]
})
export class TableTemplateComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() tableData!: any[];
  @Input() columnList!: TableColumnModel[];
  @Input() rowsPerPage = 10;
  @Input() sortField = '';
  @Input() sortOrder = 1;
  @Input() sortMode: 'single' | 'multiple' = 'single';
  @Input() multiSortMeta: { field: string, order: number, hidden?: boolean }[] = [];
  @Input() loading = true;
  @Input() isPaginationEnabled = true;
  @Input() page = 1;
  @Input() rowsPerPageOptions = [5, 10, 20, 50];
  @Input() isBoxedTable = false;
  @Input() editableTable = false;
  @Input() sortableTable = true;
  @Input() showFilters = true;
  @Input() showDeleteButton = false;
  @Input() showCaptionPanel = true;
  @Input() rowClickable = false;
  @Input() paginatorPosition: 'top' | 'bottom' | 'both' = 'bottom';
  @Input() showGlobalFilter = false;
  // columnFilterDisplay option 'menu' saves header space (single row) but filter values won't be shown by default
  @Input() columnFilterDisplay: 'row' | 'menu' = 'row';
  //custom validation function to be passed by calling component
  @Input() validateFn: (entry: any) => Promise<{ isValid: boolean, message: string }>;
  @Input() canExitEditOnSaveFn: (entry: any) => Promise<boolean>;
  @Output() actionButtonClicked = new EventEmitter<ActionButtonEvent>();
  @Output() editInputChanged = new EventEmitter<{ event: any, entry: any, column: TableColumnModel }>();
  @Output() pageDetails = new EventEmitter<{ page: number, rows: number }>();
  @ViewChild('dt') dt!: Table;
  @ViewChild('globalfilter') globalfilter!: ElementRef;
  backupDataMap: { [s: string]: any } = {};
  columnFilters: { [s: string]: { filters?: FilterMetadata[], filter?: FilterMetadata } } = {};
  multiSortData: { [s: string]: boolean } = {};
  multiSortDefault: { field: string, order: number, hidden?: boolean }[];
  isSortCleared = false;
  hiddenSortField = '';
  columnNames: string[] = [];
  uniqueDataSet: { [key: string]: Set<string> } = {};
  uniqueDataArray: { [key: string]: string[] } = {};
  first = 0;
  filterPaginationText = '';
  isColumnSorted = false;
  private isEditing = false;
  get isEditMode(): boolean {
    return this.isEditing;
  }
  static readonly IN_ARRAY = "inArray";
  static readonly DATE_RANGE = "dateRange";
  dateMatchModeOptions: { label: string, value: string }[] = [
    { label: 'Date Range', value: TableTemplateComponent.DATE_RANGE },
    { label: 'Date is', value: FilterMatchMode.DATE_IS },
    { label: 'Date is not', value: FilterMatchMode.DATE_IS_NOT },
    { label: 'Date is before', value: FilterMatchMode.DATE_BEFORE },
    { label: 'Date is after', value: FilterMatchMode.DATE_AFTER }
  ];
  matchModeStringMappings: { [s: string]: string } = {
    dateRange: '[ ]', dateBefore: '<', dateAfter: '>', dateIs: '=', dateIsNot: '!=',
    gte: '>=', gt: '>', lte: '<=', lt: '<', equals: '=', notEquals: '!='
  };

  constructor(private readonly filterService: FilterService, private readonly messageService: MessageService) { }

  ngOnInit() {
    this.registerCustomFilters();
  }

  registerCustomFilters() {
    this.filterService.register(TableTemplateComponent.IN_ARRAY, (value: string, filters: string[]): boolean => {
      if (!filters || filters.length === 0) {
        return true;
      }
      if (!value || value.trim() === '') {
        return false;
      }
      return filters.some(filterValue => value.includes(filterValue));
    });
    this.filterService.register(TableTemplateComponent.DATE_RANGE, (value: Date, filters: Date[]): boolean => {
      if (filters == null || !Array.isArray(filters) || filters.length !== 2 || filters[1] == null) {
        return true;
      }
      if (value == null) {
        return false;
      }
      return value >= filters[0] && value <= filters[1];
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['multiSortMeta']?.isFirstChange()) {
      this.multiSortDefault = this.multiSortMeta.slice();
    }
    if (this.columnNames.length === 0) {
      const editableTable = changes['editableTable']?.currentValue;
      for (const column of this.columnList) {
        this.columnNames.push(column.name);
        if (column.inputType && editableTable) {
          this.editableTable = true;
        }
        if (column.type === 'date') {
          column.calendarFormat = this.mappingAngularToPrimeNgDateFormat(column.dateFormat);
          column.dateSelectionMode = column.matchMode === 'dateRange' ? 'range' : 'single';
          column.filterDates = column.matchMode === 'dateRange' ? [] : null;
          column.matchModeOptions = this.dateMatchModeOptions;
          this.handleDateConversions(column);
        }
        else if (column.type === 'numeric') {
          this.handleNumericFixedDecimals(column);
        }
        else if (column.type === 'category' && !column.hideFilter) {
          if (column.staticList) {
            this.uniqueDataArray[column.name] = column.staticList;
          }
          else {
            this.computeUniqueSetsOnColumn(column);
          }
        }
      }
    }
    this.hiddenSortField = this.multiSortMeta.find(s => s.hidden)?.field ?? '';
    this.first = ((this.page - 1) * this.rowsPerPage);
  }

  ngAfterViewInit() {
    this.saveColumnFilters();
  }

  handleDateConversions(column: TableColumnModel) {
    for (const row of this.tableData) {
      if (row[column.name] && typeof row[column.name] === 'string') {
        //Add time component T00:00:00 to consider date string as local date
        if (column.dateOnly) {
          row[column.name] = new Date(`${row[column.name]}T00:00:00`);
        }
        else {
          row[column.name] = new Date(row[column.name]);
        }
      }
    }
  }

  handleNumericFixedDecimals(column: TableColumnModel) {
    for (const row of this.tableData) {
      if (row[column.name]) {
        row[column.name] = +row[column.name].toFixed(column.maxFractionDigits ?? 2);
      }
    }
  }

  processCategoryColumns() {
    this.uniqueDataSet = {};
    for (const column of this.columnList) {
      if (column.type === 'category' && !column.hideFilter && !column.staticList) {
        this.computeUniqueSetsOnColumn(column);
      }
    }
  }

  computeUniqueSetsOnColumn(columnToCompute: TableColumnModel) {
    if (!this.uniqueDataSet[columnToCompute.name]) {
      this.uniqueDataSet[columnToCompute.name] = new Set<string>();
    }
    for (const row of this.tableData) {
      //store unique value of every row field if either no filters applied or if all column filters except the field column match
      if (!this.dt || this.dt.totalRecords === this.tableData.length ||
        this.columnList.every(column => column.name === columnToCompute.name || column.hideFilter ||
          this.filterService.filters[column.matchMode](row[(column.filterField ?? column.name)], this.columnFilters[column.filterField ?? column.name]?.filter?.value))) {
        this.uniqueDataSet[columnToCompute.name].add(row[(columnToCompute.filterField ?? columnToCompute.name)]);
      }
    }
    this.uniqueDataArray[columnToCompute.name] = [...this.uniqueDataSet[columnToCompute.name]];
    this.uniqueDataArray[columnToCompute.name].sort((a, b) => a > b ? 1 : -1);
  }

  deepCopyObject(source: any, target: any) {
    if (source && target) {
      for (const key in source) {
        target[key] = source[key];
      }
    }
  }

  onRowEditInit(entry: any) {
    if (!this.isEditing) {
      this.isEditing = true;
      this.backupDataMap[entry.id] = {};
      this.deepCopyObject(entry, this.backupDataMap[entry.id]);
      this.actionButtonClicked.emit({ entry: entry, action: 'init' });
    }
  }

  initRowEdit(entry: any, newRowPosition: 'first' | 'last' | 'none' = 'none') {
    if (this.isEditing) {
      return;
    }
    //Add new row to primeng table data only if table references are not same (e.g. sort or filters applied)
    if (newRowPosition !== 'none' && this.tableData !== this.dt.value) {
      //If any filters are applied, clear them first before adding new row
      if (this.dt.filteredValue) {
        this.clearTable();
      }
      this.dt.totalRecords += 1;
      this.updatePaginationText();
      //Add the new item as first/last row to the table and navigate to first/last page
      if (newRowPosition === 'first') {
        this.dt.value.unshift(entry);
        this.dt.onPageChange({ first: 0, rows: this.dt.rows });
      }
      else {
        this.dt.value.push(entry);
        const firstRecordIndex = Math.floor((this.dt.value.length - 1)/this.dt.rows) * this.dt.rows;
        this.dt.onPageChange({ first: firstRecordIndex, rows: this.dt.rows });
      }
    }
    entry.isNew = newRowPosition !== 'none';
    this.dt.initRowEdit(entry);
    this.onRowEditInit(entry);
    //Scroll to cell editor which will appear at the end of table after a short 100ms delay
    setTimeout(() => document.getElementById('cellEditor')?.scrollIntoView({ behavior: 'smooth' }), 100);
  }

  async validateEntry(entry: any): Promise<{ isValid: boolean, message: string }> {
    let validationResult = { isValid: true, message: '' };
    //Basic validations like required fields and positive number check
    for (const column of this.columnList.filter(col => col.inputType)) {
      if (column.required && (entry[column.name] == null
        || (typeof entry[column.name] === 'string' && entry[column.name].trim() === ''))) {
          validationResult.message = `'${column.label}' is required`;
      }
      else if (typeof entry[column.name] === 'number') {
        if (column.checkPositive && entry[column.name] < 0) {
          validationResult.message = `'${column.label}' must be a positive number`;
        }
        else if (entry[column.name] <= column.greaterThanValue) {
          validationResult.message = `'${column.label}' must be greater than ${column.greaterThanValue}`;
        }
      }
      if (validationResult.message !== '') {
        validationResult.isValid = false;
        return validationResult;
      }
    };
    //If basic validation passes, call external validate function if provided
    if (validationResult.isValid && this.validateFn) {
      validationResult = await this.validateFn(entry);
    }
    return validationResult;
  }

  async onRowEditSave(entry: any) {
    const result = await this.validateEntry(entry);
    //Exit row edit mode if no custom canExitEditOnSaveFn is provided or if it returns true
    if (result.isValid && (!this.canExitEditOnSaveFn || await this.canExitEditOnSaveFn(entry))) {
      this.isEditing = false;
      delete this.backupDataMap[entry.id];
      this.actionButtonClicked.emit({ entry: entry, action: 'save' });
      //re-compute unique datasets for dropdown filters if new entry is added to list 
      if (entry.isNew) {
        this.processCategoryColumns();
      }
    }
    else {
      //Show error message as toast and re-enable row edit mode retaining the values
      this.dt.initRowEdit(entry);
      if (result.message) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: result.message, life: 10000 });
      }
    }
  }

  onRowEditCancel(entry: any) {
    this.deepCopyObject(this.backupDataMap[entry.id], entry);
    delete this.backupDataMap[entry.id];
    this.isEditing = false;
    this.actionButtonClicked.emit({ entry: entry, action: 'cancel' });
  }

  onRowDelete(entry: any) {
    if (!this.isEditing) {
      this.actionButtonClicked.emit({ entry: entry, action: 'delete' });
    }
  }

  //programmatic delete: need to delete from tableData and also from primeng table.value and filteredValue arrays
  deleteEntry(entry: any) {
    this.deleteItemFromArray(this.tableData, entry);
    const filteredIndex = this.deleteItemFromArray(this.dt.filteredValue, entry);
    const index = this.deleteItemFromArray(this.dt.value, entry);
    if (index >= 0 && (!filteredIndex || filteredIndex >= 0)) {
      //Decrement total records if item present in filtered view or if no filters applied
      this.dt.totalRecords -= 1;
      this.updatePaginationText();
    }
  }

  deleteItemFromArray(array: any[], entry: any) {
    const index = array?.findIndex(e => e.id === entry.id);
    if (index >= 0) {
      array?.splice(index, 1);
    }
    return index;
  }

  onInputChange(event: any, entry: any, column: TableColumnModel) {
    //To trigger events to calling component like e.g. to refresh dropdown options in other columns
    this.editInputChanged.emit({ event, entry, column });
  }

  dateFormatMappings = {
    //key is ** Angular date pipe ** format, value is ** primeng datepicker ** format
    MMMM: 'MM', //long month name
    MMM: 'M', //short month name
    MM: 'mm', //month number with padding
    M: 'm', //month number no padding
    yyyy: 'yy', //year 4 digits padded
    yy: 'y', //year 2 digits padded
    dd: 'dd', //day 2 digits with padding
    d: 'd', //day 1 digit no padding
    EEEE: 'DD', //long day name
    EEE: 'D', //short day name
    EE: 'D', //short day name
    E: 'D', //short day name
  }

  mappingAngularToPrimeNgDateFormat(dateFormat: string) {
    //we rely on splitting by space, in future we can replace all chars like comma with space
    return dateFormat.split(' ').map(part => this.dateFormatMappings[part]).join(' ');
  }

  applyGlobalFilter($event: any, matchMode: string) {
    const value = ($event.target as HTMLInputElement)?.value;
    this.dt.filterGlobal(value, matchMode);
  }

  filterApplied(_event: TableFilterEvent) {
    if (!this.isColumnSorted) {
      this.saveColumnFilters();
      this.updatePaginationText();
    }
    this.isColumnSorted = false;
  }

  augmentDateColumnFilterMethods(column: TableColumnModel, columnFilter: ColumnFilter) {
    column.dateAugmented = true;
    const onRowMatchModeChange = columnFilter.onRowMatchModeChange;
    columnFilter.onRowMatchModeChange = (matchMode: string) => {
      //Below line is important, it saves match mode modified by user whether filter is applied or cleared.
      columnFilter.matchMode = column.matchMode = matchMode;
      const currentSelectionMode = column.dateSelectionMode;
      column.dateSelectionMode = column.matchMode === 'dateRange' ? 'range' : 'single';
      //call filter method only if date selection mode hasn't changed between single and range.
      if (currentSelectionMode === column.dateSelectionMode) {
        onRowMatchModeChange.call(columnFilter, matchMode);
      }
      else {
        //Else clear filter dates array, textbox and table filter when selection mode changes
        column.filterDates = null;
        columnFilter.clearFilter();
      }
    };
    //Augment clear column filter method to also clear the filter dates text in date picker
    const onRowClearItemClick = columnFilter.onRowClearItemClick;
    columnFilter.onRowClearItemClick = () => {
      column.filterDates = null;
      onRowClearItemClick.call(columnFilter);
    };
  }

  columnFilterShow(column: TableColumnModel, columnFilter: ColumnFilter) {
    //Augment default handler methods only once for date type column filter
    if (column.type === 'date' && !column.dateAugmented) {
      this.augmentDateColumnFilterMethods(column, columnFilter);
      column.dateAugmented = true;
    }
  }

  saveColumnFilters() {
    this.columnFilters = {};
    for (const entry of Object.entries(this.dt.filters)) {
      if (Array.isArray(entry[1])) {
        this.columnFilters[entry[0]] = { filters: entry[1] };
      }
      else {
        this.columnFilters[entry[0]] = { filter: entry[1] };
      }
    }
  }

  updatePaginationText() {
    if (this.dt.totalRecords !== this.tableData.length) {
      this.filterPaginationText = `(filtered from ${this.tableData.length} total entries)`;
    }
    else if (this.filterPaginationText !== '') {
      this.filterPaginationText = '';
    }
    //Everytime when filters applied, re-compute unique datasets for dropdown filters
    this.processCategoryColumns();
  }

  onPageChange(event: any) {
    const currentPage = (event.first / event.rows) + 1;
    this.pageDetails.emit({ page: currentPage, rows: event.rows });
  }

  tableSorted(_event: any) {
    if (!this.dt) {
      return;
    }
    if (this.isSortCleared) {
      this.isSortCleared = false;
    }
    else {
      // when columns are sorted by user action, remove the default hidden field sort.
      const hiddenFieldIndex = this.dt.multiSortMeta?.findIndex(s => s.field === this.hiddenSortField);
      if (hiddenFieldIndex >= 0) {
        this.dt.multiSortMeta.splice(hiddenFieldIndex, 1);
        this.dt.sortMultiple();
      }
    }
    this.multiSortData = {};
    this.dt.multiSortMeta?.forEach(s => this.multiSortData[s.field] = true);
  }

  clearTable() {
    this.dt.clear();
    this.dt.reset();
    if (this.globalfilter) {
      this.globalfilter.nativeElement.value = '';
    }
    this.columnList.forEach(column => column.filterDates = null);
    this.resetSort();
  }

  resetSort() {
    this.isSortCleared = true;
    if (!this.multiSortMeta || this.multiSortMeta.length === 0) {
      this.dt.sortField = this.sortField;
      this.dt.sortOrder = this.sortOrder;
      this.dt.sortSingle();
    }
    else {
      // when reset, add hidden field to sort by applying copy of original default sort
      this.dt.multiSortMeta = this.multiSortDefault.slice();
      this.dt.sortMultiple();
    }
  }

  clearColumnFilter(column: TableColumnModel, columnFilter: ColumnFilter) {
    column.filterDates = null;
    columnFilter.clearFilter();
    this.saveColumnFilters();
    this.updatePaginationText();
  }

  hasActiveFilter(column: TableColumnModel) {
    const columnFilterValue = this.columnFilters[column.filterField ?? column.name]?.filter?.value;
    return Array.isArray(columnFilterValue) ? columnFilterValue.length > 0 :
      columnFilterValue != null && columnFilterValue !== '';
  }

  buttonClicked(entry: any, customAction: string) {
    this.actionButtonClicked.emit({ entry, customAction });
  }

  onRowClick(entry: any) {
    if (this.rowClickable && !this.isEditMode) {
      this.buttonClicked(entry, 'rowClick');
    }
  }

  getVisibleRowFieldValues(field: string) {
    const visibleRows = this.dt.filteredValue ?? this.dt.value;
    return visibleRows.map(row => row[field]);
  }
}